import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { CS_EMAIL, getCsPhoneDisplay, getCsWhatsAppUrl } from "../../lib/contact";
import { storefrontUrl } from "../../lib/storefront";
import { CS_HOURS, STORES, isCsOpen } from "../Footer/constants";
import { ChatIcon, HangerIcon, MailIcon, PhoneIcon, WhatsAppIcon } from "./icons";

/** Re-check the WhatsApp status this often while the page stays open. */
const STATUS_REFRESH_MS = 60 * 1000;

/** Spec 4.4: a 6px dot and 11px text; green only when it is computed open. */
const Status = ({ on, children }) => <span className={`hc-status${on ? "" : " hc-status--off"}`}>{children}</span>;

/**
 * One store row: its sigil (the footer's), name, city and office phone, then
 * icon buttons to call or chat. Gamma and Cosplay Corner have no office line,
 * so they show WhatsApp only; WhatsApp numbers are never printed, as in the
 * footer -- the button opens the right chat.
 */
const StoreRow = ({ store }) => (
  <li className="hc-shop">
    <span className="hc-shop__sigil" style={store.sigilFont ? { fontFamily: store.sigilFont } : undefined} aria-hidden="true">
      {store.sigilIcon === "hanger" ? <HangerIcon /> : store.sigil}
    </span>
    <span className="hc-shop__info">
      <span className="hc-shop__name">{store.name}</span>
      <span className="hc-shop__meta">
        {store.city}
        {store.phoneLabel ? ` · ${store.phoneLabel}` : " · WhatsApp saja"}
      </span>
    </span>
    <span className="hc-shop__actions">
      {store.phoneHref ? (
        <a href={store.phoneHref} className="hc-iconbtn" aria-label={`Telepon ${store.name}`} title={`Telepon ${store.name}`}>
          <PhoneIcon size={16} />
        </a>
      ) : null}
      {store.whatsappHref ? (
        <a
          href={store.whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="hc-iconbtn hc-iconbtn--wa"
          aria-label={`Chat WhatsApp ${store.name}`}
          title={`Chat WA Kyou (${store.name})`}
        >
          <WhatsAppIcon size={16} />
        </a>
      ) : null}
    </span>
  </li>
);

/**
 * "Belum ketemu jawabannya?" -- the CS chat and e-mail stacked on the left,
 * the stores on the right, so no card stands half empty next to a tall one.
 * Every card reads the same way: title and status, the one thing to know, a
 * line on when to use it, and its action at the bottom.
 *
 * The WhatsApp status is computed, not written: `isCsOpen` (Footer/constants,
 * mirrored from hanamaru's getCsStatus) pins the check to WIB, so a reader
 * abroad sees the hours as Jakarta keeps them. The visual spec prefers server
 * time; this uses the device clock converted to WIB, which only differs when
 * the device clock itself is wrong.
 */
const ContactBand = () => {
  const [isOpen, setIsOpen] = useState(() => isCsOpen(new Date()));

  useEffect(() => {
    const timer = window.setInterval(() => setIsOpen(isCsOpen(new Date())), STATUS_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="hc-contact-band" aria-labelledby="hc-contact-title">
      <div className="hc-container hc-contact-band__inner">
        <h2 id="hc-contact-title" className="hc-section__title">
          Belum ketemu jawabannya?
        </h2>
        <p className="hc-section__note">Team Kyou siap bantu {CS_HOURS}.</p>

        <div className="hc-contacts">
          <div className="hc-contact hc-contact--wa">
            <div className="hc-contact__head">
              <span className="hc-contact__icon">
                <ChatIcon size={22} />
              </span>
              <h3 className="hc-contact__title">Chat WhatsApp</h3>
              <Status on={isOpen}>{isOpen ? "Online sekarang" : "Sedang tutup"}</Status>
            </div>
            <p className="hc-contact__value">{getCsPhoneDisplay()}</p>
            <p className="hc-contact__desc">
              Jalur paling cepat buat urusan order kamu. Nomor yang sama juga bisa ditelepon lewat WhatsApp.
            </p>
            <div className="hc-contact__actions">
              <a className="hc-btn hc-btn--wa" href={getCsWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon size={16} />
                Chat sekarang
              </a>
              <Link to="/call-service/kyou-punya-layanan-telepon" className="hc-contact__link">
                Cara telepon CS
              </Link>
            </div>
          </div>

          <div className="hc-contact hc-contact--mail">
            <div className="hc-contact__head">
              <span className="hc-contact__icon">
                <MailIcon size={22} />
              </span>
              <h3 className="hc-contact__title">Email</h3>
              <Status on={false}>Dibalas di jam operasional</Status>
            </div>
            <p className="hc-contact__value">{CS_EMAIL}</p>
            <p className="hc-contact__desc">Buat kasus yang butuh lampiran, misalnya foto atau video kondisi item.</p>
            <div className="hc-contact__actions">
              <a className="hc-btn hc-btn--ghost" href={`mailto:${CS_EMAIL}`}>
                Kirim email
              </a>
            </div>
          </div>

          <div className="hc-contact hc-contact--shops">
            <div className="hc-contact__head">
              <span className="hc-contact__icon">
                <PhoneIcon size={22} />
              </span>
              <h3 className="hc-contact__title">Hubungi toko</h3>
              <Status on={false}>Ikut jam buka toko</Status>
            </div>
            <ul className="hc-shops">
              {STORES.map(store => (
                <StoreRow key={store.name} store={store} />
              ))}
            </ul>
            <div className="hc-contact__actions">
              <a className="hc-contact__link" href={storefrontUrl("/stores")}>
                Alamat dan 360&deg; view semua toko
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactBand;
